import * as React from "react";
import { CreateWalletKyc, ViewMe } from "@njoy/controller";
import { kycSchema } from "@njoy/common";

import { NewModal } from "../uiMerge/newModal";
import Button from "../uiMerge/newButton";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ToastMessage from "../../hooks/useToast";
import * as ROUTES from "../../constants/routes";
import { Formik, Form, Field } from "formik";
import { InputField } from "src/modules/shared/InputField";
import { openSupportEscalationMail } from "../../utils/supportEscalation";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const AccessWalletModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [supportIssue, setSupportIssue] = React.useState<{
    message: string;
    errorCode?: string;
    supportRef?: string;
  } | null>(null);
  const [errorCount, setErrorCount] = React.useState(0);

  const handleClose = () => {
    setSupportIssue(null);
    setErrorCount(0);
    onClose();
  };

  return (
    <NewModal
      isOpen={isOpen}
      onClose={handleClose}
      position="end"
      colorClass="bg-black"
      animationClass="grow-in"
      showCloseButton={false}
    >
      <ViewMe>
        {({match, loading}) => {
          if (loading) {
            return <p className="text-[#9fafb5] flex justify-center items-center h-screen">Loading...</p>;
          }

          // If NIN is null, show toast and block access
          if (match?.customer?.nin === null) {
            ToastMessage("error", "You need to verify your NIN before accessing your wallet. Please go back and verify your NIN.");
            setTimeout(() => {
              onClose();
            }, 1800);
            
            return null;
          }

          return(
            <>
              <ToastContainer />

              <CreateWalletKyc>
                {({ createWalletKyc }: { createWalletKyc: any }) => {
                  return (
                    <Formik
                      initialValues={{ 
                        nin: match?.customer?.nin || "", 
                        dob: "", 
                        bvn: "" 
                      }}
                      validationSchema={kycSchema}
                      validateOnChange={true}
                      validateOnBlur={true}
                      onSubmit={async (values, { setSubmitting }) => {
                        const { dob, bvn, nin } = values;
                        setSubmitting(true);

                        const result = await createWalletKyc({
                          nin,
                          dob,
                          bvn,
                        });
                          
                        // Success: no errors and has message
                        if (result && !result.errors && result.message) {
                          setSupportIssue(null);
                          setErrorCount(0);
                          ToastMessage("success", result.message || "Wallet accessed successfully!");

                          setTimeout(() => {
                            onClose();
                          }, 1800);
                          window.location.replace(ROUTES.VIEW_WALLET);
                          return;
                        }


                        // Error: has errors
                        if (result && result.errors) {
                          // Simplified error extraction
                          const errorMsg =
                            Array.isArray(result.errors) &&
                            result.errors.length > 0 &&
                            result.errors[0].message
                              ? result.errors[0].message
                              : "Failed to access wallet. Please check your input.";
                          setSupportIssue({
                            message: errorMsg,
                            errorCode: result.supportMeta?.code,
                            supportRef: result.supportMeta?.supportRef,
                          });
                            setErrorCount((currentCount) => currentCount + 1);
                          ToastMessage("error", errorMsg);
                          
                          return;
                        }

                        // Fallback error
                        setSupportIssue({ message: "Failed to access wallet. Please try again." });
                        setErrorCount((currentCount) => currentCount + 1);
                        ToastMessage(
                          "error",
                          "Failed to access wallet. Please try again."
                        );
                      }}
                    >
                      {({ handleSubmit, isSubmitting, values }) => (
                        <Form onSubmit={handleSubmit}>
                            {/* Modal Content */}
                          <div className="flex flex-col gap-4 px-6">
                            <div className="flex flex-col items-center gap-3">
                              <div
                              onClick={onClose}
                              className="bg-white w-[77px] h-[4px] mt-5 rounded-2xlflex items-center" />

                              <div className="flex flex-col gap-5 items-center mt-6">
                                <p className="text-white font-[900] text-[24px]">
                                  Access Your Wallet
                                </p>
                                <p className="text-white text-[16px] text-center">
                                  To continue, enter your BVN and Date of Birth. This
                                  helps us confirm your identity and keep your wallet
                                  secure.
                                </p>
                              </div>
                            </div>

                            {/* Fields */}
                            <div className="flex flex-col gap-2">
                              <div className="flex flex-col ">
                                <label className="text-[#CECECF]" htmlFor="bvn">
                                  BVN
                                </label>
                                <Field
                                  name="bvn"
                                  placeholder="BVN"
                                  className="rounded-lg bg-[#2E2E2E] p-3 text-white"
                                  component={InputField}
                                />
                              </div>

                              <div className="flex flex-col">
                                <label className="text-[#CECECF]" htmlFor="dob">
                                  Date of Birth
                                </label>
                                <Field
                                  name="dob"
                                  type="date"
                                  placeholder="Date of Birth"
                                  className="rounded-lg bg-[#2E2E2E] p-3 text-white"
                                  component={InputField}
                                />
                              </div>

                              <p className="text-[14px] text-[#8E8E8E] text-center  mb-9">
                                You can check your BVN by dialing{" "}
                                <span className="font-extrabold text-[#8E8E8E] text-[15px]">
                                  *565*0#
                                </span>{" "}
                                using the phone number you used to register your BVN.
                              </p>
                            </div>

                            {/* Buttons */}
                            <div className="flex gap-4 mt-3">
                              <Button
                                variant="outline"
                                onClick={onClose}
                                className="w-full mb-4 bg-transparent border cursor-pointer border-white text-white"
                              >
                                Go Back
                              </Button>

                              <Button
                                type="submit"
                                className="w-full mb-3 cursor-pointer bg-gradient-to-r from-[#A151F9] to-[#0045E9] text-white"
                                loading={isSubmitting}
                              >
                                Continue
                              </Button>
                            </div>

                            {supportIssue && errorCount >= 3 && (
                              <div className="mb-4">
                                <Button
                                  variant="outline"
                                  onClick={() =>
                                    openSupportEscalationMail({
                                      actionName: "Create Wallet KYC",
                                      errorMessage: supportIssue.message,
                                      errorCode: supportIssue.errorCode,
                                      supportRef: supportIssue.supportRef,
                                      operationName: "CreateWalletKycMutation",
                                      extra: {
                                        ninPresent: Boolean(values.nin),
                                        bvnLength: values.bvn?.length || 0,
                                      },
                                    })
                                  }
                                  className="w-full bg-transparent border border-[#777] text-white"
                                >
                                  Need help? Contact Support
                                </Button>
                              </div>
                            )}
                          </div>
                        </Form>
                      )}
                    </Formik>
                  );
                }}
              </CreateWalletKyc>
            </>
          );
        }}
      </ViewMe>
    </NewModal>
  );
}

export { AccessWalletModal };
